import React, { useState, useEffect } from "react";
import PageLayout from "../components/PageLayout";
import { theme } from "../theme";
import {
  BUILTIN_USERS, ROLE_COLORS, isOwner,
  getGuestAccounts, saveGuestAccounts, getAllUsers
} from "../auth";

// ── Helpers ───────────────────────────────────────────────────────────────────

const Card = ({ children, style = {} }) => (
  <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 12, padding: 20, marginBottom: 20, ...style }}>{children}</div>
);
const SectionTitle = ({ children, color = theme.colors.accent }) => (
  <div style={{ fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700, color, letterSpacing: 1, marginBottom: 14, textTransform: "uppercase" }}>{children}</div>
);
const RoleBadge = ({ role }) => {
  const color = ROLE_COLORS[role] || theme.colors.textMuted;
  return <span style={{ padding: "3px 10px", borderRadius: 4, background: `${color}18`, border: `1px solid ${color}44`, color, fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{role}</span>;
};
const Input = ({ label, value, onChange, type = "text", placeholder = "", disabled = false }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>{label}</div>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
      style={{ width: "100%", background: disabled ? theme.colors.inputBgDisabled : theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: disabled ? theme.colors.textMuted : theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none", cursor: disabled ? "not-allowed" : "text" }}
      onFocus={e => { if (!disabled) e.target.style.borderColor = theme.colors.accent; }}
      onBlur={e => e.target.style.borderColor = theme.colors.cardBorder}
    />
  </div>
);

const PAGE_PERMS = {
  hassaan: ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel", "User Management"],
  admin:   ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel", "User Management"],
  analyst: ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel"],
  guest:   ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel"],
  viewer:  ["Dashboard", "Cyber Alerts", "Reports"],
};

function generatePassword(len = 14) {
  const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ","abcdefghjkmnpqrstuvwxyz","23456789","!@#$%&*"];
  let pass = sets.map(s => s[Math.floor(Math.random() * s.length)]).join("");
  const all = sets.join("");
  for (let i = pass.length; i < len; i++) pass += all[Math.floor(Math.random() * all.length)];
  return pass.split("").sort(() => Math.random() - 0.5).join("");
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function UserManagement() {
  const currentUser = JSON.parse(sessionStorage.getItem("nexgen_user") || "{}");
  const role        = currentUser.role || "";
  const ownerAccess = isOwner(role);

  const [guestAccounts, setGuestAccounts] = useState(getGuestAccounts);
  const [activeTab, setActiveTab]         = useState("builtin");
  const [showForm, setShowForm]           = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saved, setSaved]                 = useState("");
  const [emailPreview, setEmailPreview]   = useState(null);

  const [form, setForm] = useState({
    username: "", password: generatePassword(),
    role: "guest", note: "", company: "",
    expiresAt: "", active: true,
  });

  const f = k => v => setForm(p => ({ ...p, [k]: v }));

  const notify = (msg) => { setSaved(msg); setTimeout(() => setSaved(""), 2500); };

  // ── Save guest accounts ────────────────────────────────────────────────────

  const persistGuests = (accounts) => {
    setGuestAccounts(accounts);
    saveGuestAccounts(accounts);
  };

  // ── Create guest account ──────────────────────────────────────────────────

  const handleCreate = () => {
    if (!form.username.trim()) return alert("Username is required");
    if (form.username.trim().length < 3) return alert("Username must be at least 3 characters");
    if (!form.password.trim() || form.password.trim().length < 6) return alert("Password must be at least 6 characters");

    const all = getAllUsers();
    if (all.find(u => u.username.toLowerCase() === form.username.toLowerCase())) {
      return alert("Username already exists. Choose a different one.");
    }

    const newAcc = {
      id:        Date.now(),
      username:  form.username.trim().toLowerCase().replace(/\s+/g, "_"),
      password:  form.password.trim(),
      role:      form.role,
      active:    true,
      note:      form.note.trim(),
      company:   form.company.trim(),
      expiresAt: form.expiresAt || null,
      created:   new Date().toLocaleDateString(),
      lastLogin: null,
      createdBy: currentUser.username,
    };

    const updated = [...guestAccounts, newAcc];
    persistGuests(updated);
    setShowForm(false);
    setForm({ username: "", password: generatePassword(), role: "guest", note: "", company: "", expiresAt: "", active: true });
    setEmailPreview(newAcc);
    notify("Account created!");
  };

  const handleDelete = (id) => {
    persistGuests(guestAccounts.filter(u => u.id !== id));
    setDeleteConfirm(null);
    notify("Account deleted");
  };

  const handleToggle = (id) => {
    persistGuests(guestAccounts.map(u => u.id === id ? { ...u, active: !u.active } : u));
    notify("Status updated");
  };

  const handleResetPass = (id) => {
    const newPass = generatePassword();
    persistGuests(guestAccounts.map(u => u.id === id ? { ...u, password: newPass } : u));
    notify(`Password reset — copy it now!`);
  };

  // ── Email template ─────────────────────────────────────────────────────────

  const buildEmail = (acc) => {
    const expiry = acc.expiresAt ? `\nAccess Expires: ${new Date(acc.expiresAt).toLocaleDateString("en-US", { weekday:"long", year:"numeric", month:"long", day:"numeric" })}` : "\nAccess Expires: No expiry (unlimited)";
    return {
      subject: `Your NexGen Access Credentials — ${acc.company || "NexGen Demo"}`,
      body: `Dear ${acc.company ? acc.company + " team" : "Valued User"},

You have been granted access to the NexGen Cyber Intelligence Platform — a professional cybersecurity analytics tool developed by Hassaan Alrifai.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  YOUR LOGIN CREDENTIALS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  Portal URL : https://hassaanalrifai.com/nexgen
  Username   : ${acc.username}
  Password   : ${acc.password}
  Access Role: ${acc.role.toUpperCase()}${expiry}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

WHAT YOU CAN DO:
• Upload cybersecurity datasets (CSV/Excel) from your IDS, Firewall, or SIEM
• View 20+ attack analysis charts and visualizations
• Analyze attack types, source IPs, ports, protocols, and severity
• Generate and export professional incident reports
• Use the Threat Intelligence tools: IP lookup, port intel, MITRE ATT&CK reference

IMPORTANT SECURITY NOTES:
• These credentials are for your use only — do not share them
• Uploaded datasets are session-only and are NOT stored on our servers
• Your session will expire after 8 hours of inactivity
• This is a demo/evaluation account${acc.expiresAt ? " with a limited access period" : ""}

If you have any questions or need assistance, please contact:
Hassaan Alrifai
📧 contact@hassaanalrifai.com
🌐 hassaanalrifai.com

Best regards,
Hassaan Alrifai
NexGen Cyber Intelligence Platform`
    };
  };

  // ── Access denied ─────────────────────────────────────────────────────────

  if (!ownerAccess) {
    return (
      <PageLayout title="User Management" subtitle="ACCESS CONTROL · ADMIN ONLY">
        <Card style={{ textAlign: "center", padding: "60px 40px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 18, color: theme.colors.danger, letterSpacing: 2 }}>ACCESS DENIED</div>
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginTop: 8 }}>
            This page is restricted to <strong style={{ color: theme.colors.accent }}>hassaan</strong> and <strong style={{ color: theme.colors.accent }}>admin</strong> accounts only.
          </div>
        </Card>
      </PageLayout>
    );
  }

  const tabs = [
    { id: "builtin", label: "Core Accounts" },
    { id: "guests",  label: `Guest Accounts (${guestAccounts.length})` },
    { id: "roles",   label: "Roles & Permissions" },
    { id: "security",label: "Security" },
  ];

  return (
    <PageLayout title="User Management" subtitle="ACCESS CONTROL · ROLES · PERMISSIONS · GUEST ACCOUNTS">

      {saved && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 24px", background: theme.colors.success, borderRadius: 8, color: theme.colors.buttonText, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1, boxShadow: "0 4px 20px rgba(0,230,118,0.4)" }}>
          ✓ {saved}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${theme.colors.cardBorder}`, paddingBottom: 1 }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.id ? `2px solid ${theme.colors.danger}` : "2px solid transparent",
            color: activeTab === tab.id ? theme.colors.danger : theme.colors.textMuted,
            fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer"
          }}>{tab.label.toUpperCase()}</button>
        ))}
      </div>

      {/* ═══ CORE ACCOUNTS ═══ */}
      {activeTab === "builtin" && (
        <>
          <Card style={{ borderColor: "rgba(255,184,0,0.3)", background: "rgba(255,184,0,0.04)", marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 10 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>🔒</span>
              <div style={{ fontFamily: theme.fonts.body, fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.7 }}>
                <strong style={{ color: theme.colors.warning }}>Core accounts cannot be deleted or modified here.</strong> To change passwords,
                edit the <code style={{ background: theme.colors.inputBg, padding: "1px 6px", borderRadius: 3, color: theme.colors.accent }}>BUILTIN_USERS</code> array
                in <code style={{ background: theme.colors.inputBg, padding: "1px 6px", borderRadius: 3, color: theme.colors.accent }}>src/auth.js</code> directly.
                Only the <strong style={{ color: "#FF0033" }}>hassaan</strong> and <strong style={{ color: theme.colors.danger }}>admin</strong> roles can see this page.
              </div>
            </div>
          </Card>

          <Card style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: theme.colors.inputBg }}>
                  {["Account", "Role", "Access Level", "Status", "Note"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, borderBottom: `1px solid ${theme.colors.cardBorder}`, letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BUILTIN_USERS.map((u, i) => {
                  const rc = ROLE_COLORS[u.role] || theme.colors.textMuted;
                  const isMe = u.username === currentUser.username;
                  return (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: i % 2 === 0 ? theme.colors.rowAlt : "transparent" }}>
                      <td style={{ padding: "13px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: "50%", background: `${rc}22`, border: `2px solid ${rc}44`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, color: rc }}>
                            {u.username.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontFamily: theme.fonts.body, fontWeight: 700, fontSize: 14, color: theme.colors.textPrimary }}>
                              {u.username}
                              {isMe && <span style={{ marginLeft: 6, fontSize: 10, color: theme.colors.accent, fontFamily: theme.fonts.mono }}>(you)</span>}
                            </div>
                            <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>Built-in · Created {u.created}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "13px 16px" }}><RoleBadge role={u.role} /></td>
                      <td style={{ padding: "13px 16px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary }}>
                        {PAGE_PERMS[u.role]?.join(", ")}
                      </td>
                      <td style={{ padding: "13px 16px" }}>
                        <span style={{ padding: "3px 10px", borderRadius: 4, background: "rgba(0,230,118,0.1)", border: "1px solid rgba(0,230,118,0.3)", color: theme.colors.success, fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700 }}>ACTIVE</span>
                      </td>
                      <td style={{ padding: "13px 16px", fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textMuted }}>{u.note || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* ═══ GUEST ACCOUNTS ═══ */}
      {activeTab === "guests" && (
        <>
          <Card style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 22, fontWeight: 900 }}>
                <span style={{ color: theme.colors.warning }}>{guestAccounts.length}</span>
                <span style={{ color: theme.colors.textSecondary, fontSize: 13, marginLeft: 8 }}>GUEST ACCOUNTS</span>
              </div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
                {guestAccounts.filter(u => u.active).length} active · {guestAccounts.filter(u => u.expiresAt && new Date(u.expiresAt) < new Date()).length} expired
              </div>
            </div>
            <div style={{ flex: 1 }} />
            {saved && <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.success }}>✓ {saved}</span>}
            <button onClick={() => { setShowForm(!showForm); setEmailPreview(null); }} style={{
              padding: "10px 20px",
              background: showForm ? "rgba(255,61,87,0.1)" : `linear-gradient(135deg, ${theme.colors.warning}, #CC9200)`,
              border: showForm ? `1px solid ${theme.colors.danger}` : "none",
              borderRadius: 8, color: showForm ? theme.colors.danger : theme.colors.buttonText,
              fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer"
            }}>{showForm ? "✕ CANCEL" : "+ CREATE GUEST ACCOUNT"}</button>
          </Card>

          {/* Create Form */}
          {showForm && (
            <Card style={{ borderColor: `rgba(255,184,0,0.4)`, background: "rgba(255,184,0,0.04)" }}>
              <SectionTitle color={theme.colors.warning}>NEW GUEST ACCOUNT</SectionTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Input label="USERNAME (login name)" value={form.username} onChange={f("username")} placeholder="e.g. companyxyz" />
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>PASSWORD</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={form.password} onChange={e => f("password")(e.target.value)}
                      style={{ flex: 1, background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.mono, fontSize: 13, padding: "9px 12px", outline: "none" }} />
                    <button onClick={() => f("password")(generatePassword())} style={{ padding: "9px 12px", background: "rgba(0,212,255,0.1)", border: `1px solid rgba(0,212,255,0.3)`, borderRadius: 7, color: theme.colors.accent, fontFamily: theme.fonts.mono, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>↻ Generate</button>
                  </div>
                </div>
                <Input label="COMPANY / ORGANIZATION" value={form.company} onChange={f("company")} placeholder="e.g. Acme Corp" />
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>ACCESS ROLE</div>
                  <select value={form.role} onChange={e => f("role")(e.target.value)} style={{ width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none" }}>
                    <option value="guest">Guest — All pages except User Management</option>
                    <option value="viewer">Viewer — Dashboard, Alerts, Reports only</option>
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>ACCESS EXPIRY (optional)</div>
                  <input type="date" value={form.expiresAt} onChange={e => f("expiresAt")(e.target.value)} min={new Date().toISOString().split("T")[0]}
                    style={{ width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.mono, fontSize: 13, padding: "9px 12px", outline: "none" }} />
                </div>
                <Input label="NOTES (internal, not shown to user)" value={form.note} onChange={f("note")} placeholder="e.g. Prospect from Dubai — 2-week trial" />
              </div>
              <button onClick={handleCreate} style={{ padding: "11px 28px", background: `linear-gradient(135deg, ${theme.colors.warning}, #CC9200)`, border: "none", borderRadius: 8, color: theme.colors.buttonText, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1, cursor: "pointer" }}>
                CREATE ACCOUNT & GENERATE EMAIL
              </button>
            </Card>
          )}

          {/* Email Preview */}
          {emailPreview && (() => {
            const email = buildEmail(emailPreview);
            return (
              <Card style={{ borderColor: "rgba(0,230,118,0.4)", background: "rgba(0,230,118,0.04)" }}>
                <SectionTitle color={theme.colors.success}>✓ ACCOUNT CREATED — EMAIL READY TO SEND</SectionTitle>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 4 }}>EMAIL SUBJECT</div>
                  <div style={{ fontFamily: theme.fonts.body, fontSize: 13, color: theme.colors.textPrimary, background: theme.colors.inputBg, borderRadius: 6, padding: "8px 12px" }}>{email.subject}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 4 }}>EMAIL BODY</div>
                  <pre style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textSecondary, background: theme.colors.inputBg, borderRadius: 8, padding: "16px", whiteSpace: "pre-wrap", lineHeight: 1.7, maxHeight: 360, overflowY: "auto", border: `1px solid ${theme.colors.cardBorder}` }}>
                    {email.body}
                  </pre>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button onClick={() => { navigator.clipboard.writeText(email.body); notify("Email body copied!"); }} style={{ padding: "9px 18px", background: "rgba(0,230,118,0.15)", border: `1px solid rgba(0,230,118,0.4)`, borderRadius: 7, color: theme.colors.success, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
                    COPY EMAIL BODY
                  </button>
                  <button onClick={() => { navigator.clipboard.writeText(email.subject); notify("Subject copied!"); }} style={{ padding: "9px 18px", background: "rgba(0,212,255,0.1)", border: `1px solid rgba(0,212,255,0.3)`, borderRadius: 7, color: theme.colors.accent, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
                    COPY SUBJECT
                  </button>
                  <a href={`mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`} style={{ textDecoration: "none" }}>
                    <button style={{ padding: "9px 18px", background: `linear-gradient(135deg, ${theme.colors.success}, #00B85A)`, border: "none", borderRadius: 7, color: theme.colors.buttonText, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
                      OPEN IN MAIL APP ↗
                    </button>
                  </a>
                  <button onClick={() => setEmailPreview(null)} style={{ padding: "9px 14px", background: "transparent", border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 11, cursor: "pointer" }}>✕</button>
                </div>
              </Card>
            );
          })()}

          {/* Guest accounts table */}
          {guestAccounts.length === 0 ? (
            <Card style={{ textAlign: "center", padding: "40px" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>◎</div>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 14, color: theme.colors.textSecondary, letterSpacing: 2 }}>NO GUEST ACCOUNTS YET</div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginTop: 6 }}>Click "Create Guest Account" to add remote demo users</div>
            </Card>
          ) : (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: theme.colors.inputBg }}>
                      {["Account", "Company", "Role", "Password", "Expires", "Status", "Created By", "Actions"].map(h => (
                        <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, borderBottom: `1px solid ${theme.colors.cardBorder}`, whiteSpace: "nowrap", letterSpacing: 1 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {guestAccounts.map((u, i) => {
                      const expired  = u.expiresAt && new Date(u.expiresAt) < new Date();
                      const rc       = ROLE_COLORS[u.role] || theme.colors.textMuted;
                      return (
                        <tr key={u.id} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: expired ? "rgba(255,61,87,0.04)" : i % 2 === 0 ? theme.colors.rowAlt : "transparent" }}>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontFamily: theme.fonts.body, fontWeight: 700, fontSize: 13, color: expired ? theme.colors.textMuted : theme.colors.textPrimary }}>{u.username}</div>
                            {u.note && <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>{u.note}</div>}
                          </td>
                          <td style={{ padding: "11px 14px", fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textSecondary }}>{u.company || "—"}</td>
                          <td style={{ padding: "11px 14px" }}><RoleBadge role={u.role} /></td>
                          <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textSecondary }}>{u.password}</td>
                          <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: expired ? theme.colors.danger : theme.colors.textMuted }}>
                            {u.expiresAt ? new Date(u.expiresAt).toLocaleDateString() : "No expiry"}
                            {expired && <div style={{ fontSize: 9, color: theme.colors.danger }}>EXPIRED</div>}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <span style={{ padding: "2px 8px", borderRadius: 4, background: !u.active || expired ? "rgba(255,61,87,0.1)" : "rgba(0,230,118,0.1)", border: `1px solid ${!u.active || expired ? "rgba(255,61,87,0.3)" : "rgba(0,230,118,0.3)"}`, color: !u.active || expired ? theme.colors.danger : theme.colors.success, fontFamily: theme.fonts.mono, fontSize: 9, fontWeight: 700 }}>
                              {expired ? "EXPIRED" : u.active ? "ACTIVE" : "DISABLED"}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>{u.createdBy}</td>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                              <button onClick={() => setEmailPreview(u)} style={{ padding: "4px 10px", background: "rgba(0,230,118,0.1)", border: `1px solid rgba(0,230,118,0.3)`, borderRadius: 5, color: theme.colors.success, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>EMAIL</button>
                              <button onClick={() => handleToggle(u.id)} style={{ padding: "4px 10px", background: "rgba(255,184,0,0.1)", border: `1px solid rgba(255,184,0,0.3)`, borderRadius: 5, color: theme.colors.warning, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>
                                {u.active ? "DISABLE" : "ENABLE"}
                              </button>
                              <button onClick={() => handleResetPass(u.id)} style={{ padding: "4px 10px", background: "rgba(0,212,255,0.1)", border: `1px solid rgba(0,212,255,0.3)`, borderRadius: 5, color: theme.colors.accent, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>RESET PW</button>
                              {deleteConfirm === u.id ? (
                                <>
                                  <button onClick={() => handleDelete(u.id)} style={{ padding: "4px 10px", background: "rgba(255,61,87,0.3)", border: `1px solid ${theme.colors.danger}`, borderRadius: 5, color: theme.colors.danger, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>CONFIRM</button>
                                  <button onClick={() => setDeleteConfirm(null)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 5, color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer" }}>✕</button>
                                </>
                              ) : (
                                <button onClick={() => setDeleteConfirm(u.id)} style={{ padding: "4px 10px", background: "rgba(255,61,87,0.1)", border: `1px solid rgba(255,61,87,0.3)`, borderRadius: 5, color: theme.colors.danger, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>DELETE</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ═══ ROLES TAB ═══ */}
      {activeTab === "roles" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {Object.entries(PAGE_PERMS).map(([role, pages]) => {
            const color = ROLE_COLORS[role] || theme.colors.textMuted;
            const count = getAllUsers().filter(u => u.role === role).length;
            const allPages = ["Dashboard","Attack Analytics","Cyber Alerts","Reports","Threat Intel","User Management"];
            return (
              <Card key={role} style={{ borderColor: `${color}33`, background: `${color}07` }}>
                <div style={{ fontFamily: theme.fonts.display, fontSize: 16, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase" }}>{role}</div>
                <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, marginTop: 3, marginBottom: 14 }}>{count} account{count !== 1 ? "s" : ""}</div>
                {allPages.map(page => {
                  const has = pages.includes(page);
                  return (
                    <div key={page} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                      <span style={{ color: has ? theme.colors.success : theme.colors.danger, fontSize: 12, flexShrink: 0 }}>{has ? "✓" : "✗"}</span>
                      <span style={{ fontFamily: theme.fonts.body, fontSize: 12, color: has ? theme.colors.textSecondary : theme.colors.textMuted }}>{page}</span>
                    </div>
                  );
                })}
              </Card>
            );
          })}
        </div>
      )}

      {/* ═══ SECURITY TAB ═══ */}
      {activeTab === "security" && (
        <>
          <Card>
            <SectionTitle>Security Checklist</SectionTitle>
            {[
              { label: "User Management visible to hassaan & admin only",   ok: true,  note: "Guest, viewer, and analyst accounts cannot see this page" },
              { label: "Guest accounts cannot access User Management",       ok: true,  note: "Route guard enforced in App.js — redirect to Dashboard" },
              { label: "Datasets are session-only (not saved to disk)",      ok: true,  note: "All uploaded data lives in React memory — cleared on tab close" },
              { label: "Session expiry after 8 hours",                      ok: true,  note: "Users are automatically logged out after 8 hours" },
              { label: "Guest account expiry dates supported",               ok: true,  note: "Set expiry when creating guest accounts" },
              { label: "HTTPS on production host",                           ok: false, note: "Set up HTTPS when deploying to hassaanalrifai.com — see HOSTING.md" },
              { label: "Backend JWT auth for production",                    ok: false, note: "Currently frontend auth — safe locally, upgrade before public hosting" },
              { label: "Rate limiting on login attempts",                    ok: false, note: "Add server-side rate limiting when hosting publicly" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: i < 7 ? `1px solid ${theme.colors.cardBorder}22` : "none" }}>
                <span style={{ fontSize: 18, color: item.ok ? theme.colors.success : theme.colors.warning, flexShrink: 0 }}>{item.ok ? "✓" : "⚠"}</span>
                <div>
                  <div style={{ fontFamily: theme.fonts.body, fontWeight: 600, fontSize: 13, color: item.ok ? theme.colors.textPrimary : theme.colors.warning }}>{item.label}</div>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>{item.note}</div>
                </div>
              </div>
            ))}
          </Card>

          <Card style={{ borderColor: "rgba(255,61,87,0.3)", background: "rgba(255,61,87,0.04)" }}>
            <SectionTitle color={theme.colors.danger}>⚡ Before Going Public</SectionTitle>
            {[
              "Change the hassaan and admin passwords in src/auth.js",
              "Enable HTTPS via Let's Encrypt (free) on your server",
              "Restrict CORS in main.py to only allow your domain",
              "Migrate to backend JWT auth (see HOSTING.md for full guide)",
              "Set expiry dates on all guest accounts",
              "Remove or disable any test accounts",
              "Set up server-side rate limiting (nginx or FastAPI middleware)",
            ].map((rule, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, fontFamily: theme.fonts.body, fontSize: 13, color: theme.colors.textSecondary }}>
                <span style={{ color: theme.colors.danger, flexShrink: 0 }}>{i + 1}.</span>
                {rule}
              </div>
            ))}
          </Card>
        </>
      )}

      <div style={{ textAlign: "center", marginTop: 24, fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, paddingBottom: 20 }}>
        NexGen by Hassaan Alrifai · © {new Date().getFullYear()} hassaanalrifai.com
      </div>
    </PageLayout>
  );
}
